import axios from "axios";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BASE_URL}/api`,
  timeout: 30000,
});

// ── Silent token refresh (keeps session alive during active work) ───────────
let lastActivity = Date.now();
const REFRESH_THRESHOLD = 20 * 60 * 1000; // 20 minutes — refresh proactively before expiry

function trackActivity() {
  lastActivity = Date.now();
}

// Track user activity
if (typeof window !== "undefined") {
  ["click", "keydown", "scroll", "mousemove", "touchstart"].forEach((evt) =>
    window.addEventListener(evt, trackActivity, { passive: true })
  );
}

// Proactive silent refresh — runs every 15 min, refreshes if user was active
let silentRefreshTimer = null;
function startSilentRefresh() {
  if (silentRefreshTimer) return;
  silentRefreshTimer = setInterval(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    const sinceActivity = Date.now() - lastActivity;
    // Only refresh if user was active in the last 20 minutes
    if (sinceActivity < REFRESH_THRESHOLD) {
      try {
        const res = await axios.post(
          `${BASE_URL}/api/auth/refresh`,
          {},
          { withCredentials: true, params: { _t: Date.now() } }
        );
        if (res.data.access_token) {
          localStorage.setItem("token", res.data.access_token);
          localStorage.setItem("token_ts", String(Date.now()));
        }
      } catch {
        // Silent fail — the 401 interceptor handles real expiry
      }
    }
  }, 15 * 60 * 1000); // every 15 minutes
}

if (typeof window !== "undefined" && localStorage.getItem("token")) {
  startSilentRefresh();
}

// ── Auth token interceptor ──────────────────────────────────────────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    startSilentRefresh();
  }
  if (config.method === "get") {
    config.params = { ...config.params, _t: Date.now() };
  }
  return config;
});

// ── Refresh-token queue ─────────────────────────────────────────────────────
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;

    const isAuthEndpoint =
      originalRequest?.url?.includes("/auth/login") ||
      originalRequest?.url?.includes("/auth/refresh") ||
      originalRequest?.url?.includes("/auth/forgot");

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const res = await axios.post(
          `${BASE_URL}/api/auth/refresh`,
          {},
          { withCredentials: true, params: { _t: Date.now() } }
        );
        const newToken = res.data.access_token;
        localStorage.setItem("token", newToken);
        localStorage.setItem("token_ts", String(Date.now()));
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        localStorage.removeItem("token");
        localStorage.removeItem("token_ts");
        if (silentRefreshTimer) {
          clearInterval(silentRefreshTimer);
          silentRefreshTimer = null;
        }
        window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
